'use client';

import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';

import { SubtleEntrance } from '@/components/ui/subtle-entrance';
import { useNearViewport } from '@/hooks/use-near-viewport';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

/* Localized copy for this section. Card titles are indexed to match CARDS
 * order below. English is the source of truth; Arabic is native Saudi. */
const COPY = {
  en: {
    eyebrow: 'Customer Stories',
    headingA: 'Hear it straight from the teams',
    headingB: 'running on Open',
    seeAll: 'See all stories',
    featured: 'Featured story',
    nextStory: 'Next story',
    showStory: 'Show story',
    titles: [
      'How FareHarbor scaled global tour operator support with Open',
      'How TicketSwap hit an all-time high CSAT with Open',
      'How Mollie handles 250K+ businesses with full compliance',
      'How MoneyGram supports 55M customers across 200+ countries',
    ],
  },
  ar: {
    eyebrow: 'قصص عملائنا',
    headingA: 'ما يحتاج تسمعها منّا،',
    headingB: 'اسمعها من شركاء النجاح اللي يستخدمون Open',
    seeAll: 'شوف كل القصص',
    featured: 'قصة مختارة',
    nextStory: 'القصة التالية',
    showStory: 'اعرض القصة',
    titles: [
      'كيف أتمتت قيود ٨٥٪ من دعم +٢٥ ألف شركة',
      'كيف أتمتت OTO ٧٧٪ من دعمها مع Open',
      'كيف تدير منصة سيارة دعم عملائها مع Open',
      'كيف تخدم Mollie +٢٥٠ ألف شركة بامتثال كامل',
      'كيف تدعم MoneyGram ٥٥ مليون عميل في +٢٠٠ دولة',
    ],
  },
};

type Copy = (typeof COPY)['en'];

const useCopy = (): Copy => {
  const { locale } = useTranslation();
  return locale === 'ar' ? COPY.ar : COPY.en;
};

export interface CustomerVideoCard {
  /** Stable unique identifier — used as the React key. Kept separate from
   * `href` because more than one card can legitimately point at the same
   * destination (e.g. a customer without its own story page yet), and a
   * duplicated key breaks list reconciliation and the featured crossfade. */
  id: string;
  /** Headline shown over the video */
  title: string;
  /** Destination of the card link */
  href: string;
  /** Path to the MP4 background video (in /public) */
  videoSrc: string;
  /** Optional poster frame shown before the video plays */
  poster?: string;
  /** Customer logo shown at the bottom of the card */
  logoSrc: string;
  /** Alt text for the logo */
  logoAlt: string;
  /** Optional logo width override in px (defaults to 96) */
  logoWidth?: number;
  /**
   * If set, the video uses hover-to-play behavior and freezes at this second
   * (e.g. 2.14) whenever the card is not hovered. If omitted, video autoplays.
   */
  freezeAt?: number;
  /** CSS object-position override for the video (default: "center") */
  objectPosition?: string;
}

/* The featured customers differ by market: the English site leads with
 * FareHarbor and TicketSwap, the Arabic site with Qoyod and OTO. Mollie and
 * MoneyGram are global and appear on both. Titles come from COPY.titles by
 * index, so the two arrays must stay the same length and order. */
export const CARDS: Record<'en' | 'ar', CustomerVideoCard[]> = {
  en: [
    {
      id: 'fareharbor',
      title: 'How FareHarbor scaled global tour operator support with Open',
      href: '/customer-stories/fareharbor',
      videoSrc: '/home/stories/fareharbor.mp4',
      logoSrc: '/logos/logo-fareharbor.svg',
      logoAlt: 'FareHarbor',
      logoWidth: 140,
      freezeAt: 2.14,
    },
    {
      id: 'ticketswap',
      title: 'How TicketSwap hit an all-time high CSAT with Open',
      href: '/customer-stories/ticketswap',
      videoSrc: '/home/stories/ticketswap.mp4',
      logoSrc: '/logos/logo-ticketswap.svg',
      logoAlt: 'TicketSwap',
      logoWidth: 120,
      freezeAt: 2.26,
      objectPosition: '30% center',
    },
    {
      id: 'mollie',
      title: 'How Mollie handles 250K+ businesses with full compliance',
      href: '/customer-stories/mollie',
      videoSrc: '/home/features/mollie-building.mp4',
      logoSrc: '/logos/logo-mollie.svg',
      logoAlt: 'Mollie',
      logoWidth: 96,
      freezeAt: 2,
    },
    {
      id: 'moneygram',
      title: 'How MoneyGram supports 55M customers across 200+ countries',
      href: '/customer-stories/moneygram',
      videoSrc: '/home/features/moneygram-shot.mp4',
      logoSrc: '/logos/logo-moneygram.svg',
      logoAlt: 'MoneyGram',
      logoWidth: 140,
      freezeAt: 2,
    },
  ],
  ar: [
    {
      id: 'qoyod',
      title: 'How Qoyod automates 85% of support for 25,000+ businesses',
      href: '/customer-stories/qoyod',
      videoSrc: '/home/stories/qoyod-v2.mp4',
      logoSrc: '/logos/logo-qoyod.png',
      logoAlt: 'Qoyod',
      logoWidth: 120,
      freezeAt: 2.14,
    },
    {
      id: 'oto',
      title: 'How OTO is Automating 77% of Support with Open',
      href: '/customer-stories/oto',
      videoSrc: '/home/stories/oto-v2.mp4',
      logoSrc: '/logos/logo-oto.svg',
      logoAlt: 'OTO',
      logoWidth: 110,
      freezeAt: 2.26,
    },
    /* Arabic-only: the interview is spoken in Arabic, so the card lives in
     * this array alone and never in CARDS.en. Placed after the established
     * Qoyod/OTO pair — move it to the front if it should lead /ar. */
    {
      id: 'syarah',
      title: 'How Syarah runs customer support with Open',
      href: '/customer-stories/syarah',
      videoSrc: '/home/stories/syarah.mp4',
      poster: '/home/stories/syarah-poster.jpg',
      logoSrc: '/logos/logo-syarah.svg',
      logoAlt: 'Syarah',
      logoWidth: 100,
      // The Open | سيارة lockup sits over the speaker from frame 0 to ~3.5s,
      // and he is mid-sentence through most of what follows; 150s is the
      // first clean, settled frame for the card to rest on.
      freezeAt: 150,
    },
    {
      id: 'mollie',
      title: 'How Mollie handles 250K+ businesses with full compliance',
      href: '/customer-stories/mollie',
      videoSrc: '/home/features/mollie-building.mp4',
      logoSrc: '/logos/logo-mollie.svg',
      logoAlt: 'Mollie',
      logoWidth: 96,
      freezeAt: 2,
    },
    {
      id: 'moneygram',
      title: 'How MoneyGram supports 55M customers across 200+ countries',
      href: '/customer-stories/moneygram',
      videoSrc: '/home/features/moneygram-shot.mp4',
      logoSrc: '/logos/logo-moneygram.svg',
      logoAlt: 'MoneyGram',
      logoWidth: 140,
      freezeAt: 2,
    },
  ],
};

const CustomerVideoCardItem = ({
  card,
  index,
}: {
  card: CustomerVideoCard;
  index: number;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLAnchorElement>(null);
  const [hasVideo, setHasVideo] = useState(true);
  const hoverMode = card.freezeAt !== undefined;
  const freezeAt = card.freezeAt ?? 0;
  // These cards sit thousands of pixels down the page. Mounting them at load
  // decoded every clip up front; the card's own dark background stands in
  // until the video is worth paying for.
  const nearViewport = useNearViewport(cardRef);
  const showVideo = hasVideo && nearViewport;

  // Detect missing video file – swap to a clean dark fallback instead of broken playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleError = () => setHasVideo(false);
    video.addEventListener('error', handleError, true);
    return () => video.removeEventListener('error', handleError, true);
  }, [showVideo]);

  // Seek to the freeze frame once metadata is ready
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hoverMode) return;

    const seekToFreeze = () => {
      try {
        video.currentTime = freezeAt;
      } catch {
        // ignore: some browsers throw if seeking before fully ready
      }
    };

    if (video.readyState >= 1) {
      seekToFreeze();
    } else {
      video.addEventListener('loadedmetadata', seekToFreeze, { once: true });
    }
    return () => video.removeEventListener('loadedmetadata', seekToFreeze);
  }, [hoverMode, freezeAt, showVideo]);

  // Hover-to-play on the card link; freeze-frame on leave
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hoverMode) return;

    const link = video.closest('a');
    if (!link) return;

    const handleEnter = () => {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    const handleLeave = () => {
      video.pause();
      try {
        video.currentTime = freezeAt;
      } catch {
        // ignore
      }
    };

    link.addEventListener('mouseenter', handleEnter);
    link.addEventListener('mouseleave', handleLeave);
    link.addEventListener('focusin', handleEnter);
    link.addEventListener('focusout', handleLeave);
    return () => {
      link.removeEventListener('mouseenter', handleEnter);
      link.removeEventListener('mouseleave', handleLeave);
      link.removeEventListener('focusin', handleEnter);
      link.removeEventListener('focusout', handleLeave);
    };
  }, [hoverMode, freezeAt, showVideo]);

  return (
    <SubtleEntrance delay={index * 0.08}>
      <Link
        ref={cardRef}
        href={card.href}
        className="radius-18 group/card relative block aspect-[1/1.66] overflow-hidden bg-neutral-900 ring-1 ring-white/5 transition-all duration-300 [-webkit-mask-image:radial-gradient(#fff,#000)] [mask-image:radial-gradient(#fff,#000)] [transform:translateZ(0)] hover:ring-white/15"
      >
        {showVideo && (
          <video
            ref={videoRef}
            src={hoverMode ? `${card.videoSrc}#t=${freezeAt}` : card.videoSrc}
            poster={card.poster}
            autoPlay={!hoverMode}
            loop={!hoverMode}
            muted
            playsInline
            preload="metadata"
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-[1.2s] ease-out group-hover/card:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover/card:scale-100"
            style={
              card.objectPosition
                ? { objectPosition: card.objectPosition }
                : undefined
            }
          />
        )}

        {/* Dark gradients for legibility */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.25) 30%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.95) 100%)',
          }}
        />

        {/* Title + arrow */}
        <div className="relative z-10 p-5">
          <h3 className="max-w-[92%] text-base text-white sm:text-[15px] md:text-base">
            {card.title}
          </h3>
          <span
            aria-hidden="true"
            className={cn(
              'mt-4 flex h-8 w-8 items-center justify-center rounded-full bg-white text-neutral-900 backdrop-blur-md',
              'opacity-0 -translate-y-1 transition-all duration-300',
              'group-hover/card:opacity-100 group-hover/card:translate-y-0',
              'motion-reduce:transition-none motion-reduce:group-hover/card:translate-y-0',
            )}
          >
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>

        {/* Logo pinned to bottom */}
        <div className="absolute inset-x-0 bottom-0 z-10 p-5">
          <Image
            src={card.logoSrc}
            alt={card.logoAlt}
            width={card.logoWidth ?? 96}
            height={28}
            className="h-7 w-auto object-contain object-left opacity-90 [filter:brightness(0)_invert(1)]"
          />
        </div>
      </Link>
    </SubtleEntrance>
  );
};

export const CustomerVideos = ({
  hideEyebrow = false,
}: {
  /** Hide the small "Customer Stories" eyebrow above the heading. */
  hideEyebrow?: boolean;
} = {}) => {
  const { locale } = useTranslation();
  const copy = useCopy();
  // Each market has its own featured customers; titles are overlaid by index.
  const cards = (locale === 'ar' ? CARDS.ar : CARDS.en).map((card, i) => ({
    ...card,
    title: copy.titles[i] ?? card.title,
  }));

  return (
    <section className="relative bg-[#fafaf9] py-16 md:py-32">
      <div className="container">
        <SubtleEntrance className="mb-12 flex flex-col gap-6 md:mb-16 md:flex-row md:items-end md:justify-between">
          <div className="!ml-0 !pl-0">
            {!hideEyebrow && (
              <p className="mb-4 text-xs font-medium tracking-widest rtl:tracking-normal text-black/40 uppercase">
                {copy.eyebrow}
              </p>
            )}
            <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
              {copy.headingA}
              <br className="hidden md:inline" /> {copy.headingB}
            </h2>
          </div>
          <Link
            href="/customer-stories"
            className="group inline-flex items-center gap-1.5 text-sm font-medium text-black/60 transition-colors hover:text-black"
          >
            {copy.seeAll}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </SubtleEntrance>

        <SubtleEntrance className="mb-4 md:mb-6">
          <FeaturedStory cards={cards} copy={copy} />
        </SubtleEntrance>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((card, index) => (
            <CustomerVideoCardItem key={card.id} card={card} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

const FeaturedStory = ({
  cards,
  copy,
}: {
  cards: CustomerVideoCard[];
  copy: Copy;
}) => {
  const { locale } = useTranslation();
  const isRtl = locale === 'ar';
  const [index, setIndex] = useState(0);
  const card = cards[index];

  const next = () => setIndex((i) => (i + 1) % cards.length);

  return (
    <div
      className={cn(
        'radius-18 group/feature relative w-full overflow-hidden bg-neutral-900 ring-1 ring-white/5 transition-all duration-300',
        'aspect-[16/9] md:aspect-[21/9]',
        // A composited layer's rounded overflow clip does not antialias
        // against its own background, so the near-black card leaks at the
        // corners. A mask forces a real masked clip, which does — and which
        // also applies to the transform-animating video inside.
        '[-webkit-mask-image:radial-gradient(#fff,#000)] [mask-image:radial-gradient(#fff,#000)] [transform:translateZ(0)]',
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={card.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <FeaturedStoryVideo src={card.videoSrc} freezeAt={card.freezeAt} />
        </motion.div>
      </AnimatePresence>

      {/* Dark gradient for legibility */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            // The headline and logo above this scrim are direction-aware, so the
            // gradient must be too — a fixed `to right` puts the Arabic eyebrow on
            // the transparent end and the logo on the dark end, i.e. both backwards.
            `linear-gradient(to ${isRtl ? 'left' : 'right'}, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 35%, rgba(0,0,0,0.1) 60%, rgba(0,0,0,0) 100%)`,
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2"
        style={{
          background:
            'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 100%)',
        }}
      />

      {/* Story content (eyebrow on top-left) */}
      <Link
        href={card.href}
        className="absolute inset-y-0 z-10 flex w-full max-w-[640px] flex-col justify-start p-6 ltr:left-0 rtl:right-0 sm:p-10 md:p-12"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${card.id}-text`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-3"
          >
            <span className="text-[11px] font-medium uppercase tracking-[0.18em] rtl:tracking-normal text-white/60">
              {copy.featured}
            </span>
          </motion.div>
        </AnimatePresence>
      </Link>

      {/* Logo pinned to top-right */}
      <div className="pointer-events-none absolute top-6 z-10 ltr:right-6 rtl:left-6 sm:top-10 sm:ltr:right-10 sm:rtl:left-10 md:top-12 md:ltr:right-12 md:rtl:left-12">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${card.id}-logo`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, delay: 0.05 }}
          >
            <Image
              src={card.logoSrc}
              alt={card.logoAlt}
              width={card.logoWidth ?? 120}
              height={32}
              className="h-8 w-auto object-contain object-right opacity-95 [filter:brightness(0)_invert(1)]"
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Next button */}
      <button
        type="button"
        onClick={next}
        aria-label={`${copy.nextStory} (${((index + 1) % cards.length) + 1} / ${cards.length})`}
        className={cn(
          'group/next absolute top-1/2 z-20 -translate-y-1/2 ltr:right-5 rtl:left-5',
          'flex h-12 w-12 items-center justify-center rounded-full',
          'bg-white/15 text-white backdrop-blur-md transition-all duration-200',
          'hover:bg-white hover:text-neutral-900 hover:scale-105',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70',
          'md:h-14 md:w-14 md:ltr:right-10 md:rtl:left-10',
        )}
      >
        <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover/next:translate-x-0.5 rtl:rotate-180 rtl:group-hover/next:-translate-x-0.5" />
      </button>

      {/* Pagination dots */}
      <div className="absolute bottom-5 z-20 flex items-center gap-1.5 ltr:right-5 rtl:left-5 md:bottom-7 md:ltr:right-10 md:rtl:left-10">
        {cards.map((c, i) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`${copy.showStory} ${i + 1}`}
            className={cn(
              'h-1 rounded-full transition-all duration-300',
              i === index
                ? 'w-6 bg-white'
                : 'w-2 bg-white/30 hover:bg-white/50',
            )}
          />
        ))}
      </div>
    </div>
  );
};

const FeaturedStoryVideo = ({
  src,
  freezeAt,
}: {
  src: string;
  freezeAt?: number;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const holderRef = useRef<HTMLDivElement>(null);
  const nearViewport = useNearViewport(holderRef);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const seek = () => {
      if (freezeAt !== undefined) {
        try {
          video.currentTime = freezeAt;
        } catch {
          // ignore
        }
      }
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    if (video.readyState >= 1) seek();
    else video.addEventListener('loadedmetadata', seek, { once: true });
    return () => video.removeEventListener('loadedmetadata', seek);
  }, [src, freezeAt, nearViewport]);

  // The holder keeps the observer target in the tree while the video itself
  // stays unmounted; the parent already reserves the box with an aspect ratio,
  // so nothing shifts when it arrives.
  return (
    <div ref={holderRef} className="absolute inset-0">
      {nearViewport && (
        <video
          ref={videoRef}
          src={freezeAt !== undefined ? `${src}#t=${freezeAt}` : src}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
    </div>
  );
};

export default CustomerVideos;
